
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { AddGuardFormProps } from "./types";
import { useGuardForm } from "./useGuardForm";
import {
  PersonalInfoFields,
  ContactFields,
  IdentificationFields,
  DateFields,
  AdditionalFields,
} from "./FormFields";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { SlideMenu } from "@/components/common/SlideMenu";
import { useLanguage } from "@/contexts/LanguageContext";

// Wrap the form content with the language provider and context
const FormContent = ({ teams, onSuccess, onClose, prepopulatedData }: AddGuardFormProps) => {
  const {
    formData,
    isAdding,
    handleInputChange,
    handleTeamChange,
    handleSubmit,
    handleCancel,
  } = useGuardForm(onSuccess, onClose, prepopulatedData);
  
  const { t } = useLanguage();

  return (
    <div className="space-y-4 mt-4 max-h-[70vh] overflow-y-auto px-1">
      <div className="flex justify-end mb-4">
        <SlideMenu title="Settings" />
      </div>
      
      <div className="bg-blue-50 p-4 rounded-md mb-6">
        <h3 className="text-sm font-medium text-blue-800">
          {prepopulatedData ? "Information from Interview" : "Important"}
        </h3>
        <p className="text-xs text-blue-700 mt-1">
          {prepopulatedData ? (
            <>
              The information below has been pre-filled from the candidate's interviews. 
              Please review and modify as needed. 
              Fields marked with <span className="text-red-500">*</span> are required.
            </>
          ) : (
            <>
              Fields marked with <span className="text-red-500">*</span> are required. 
              The VAT is used to generate guard credentials, so please ensure it's entered correctly.
            </>
          )}
        </p>
      </div>
      
      <PersonalInfoFields 
        formData={formData} 
        handleInputChange={handleInputChange} 
      />
      <ContactFields 
        formData={formData} 
        handleInputChange={handleInputChange} 
      />
      <IdentificationFields 
        formData={formData} 
        handleInputChange={handleInputChange} 
      />
      <DateFields 
        formData={formData} 
        handleInputChange={handleInputChange} 
      />
      <AdditionalFields
        formData={formData}
        handleInputChange={handleInputChange}
        handleTeamChange={handleTeamChange}
        teams={teams}
      />
      
      <div className="flex items-center space-x-2 mt-4 p-4 bg-amber-50 border border-amber-200 rounded-md">
        <Checkbox
          id="manual-hire"
          checked={formData.isManualHire}
          onCheckedChange={(checked) => {
            handleInputChange('isManualHire')({ 
              target: { value: checked } 
            } as any);
          }}
        />
        <label htmlFor="manual-hire" className="text-sm font-medium text-amber-800">
          Manual Hire - Hired directly without workflow
        </label>
      </div>
      
      <div className="flex gap-2 mt-6">
        <Button 
          className="flex-1" 
          onClick={handleSubmit}
          disabled={isAdding}
        >
          {isAdding ? "Adding..." : (formData.isManualHire ? "Add as Hired" : "Add Guard")}
        </Button>
        
        {isAdding && (
          <Button 
            variant="outline"
            className="flex-1" 
            onClick={handleCancel}
          >
            Cancel
          </Button>
        )}
      </div>
    </div>
  );
};

// Main component that provides the language context
export const AddGuardForm = (props: AddGuardFormProps) => {
  return (
    <LanguageProvider>
      <FormContent {...props} />
    </LanguageProvider>
  );
};
